/**
 * MultiAgentCompareView — Monaco compare for a MULTI-AGENT proposed artifact.
 *
 * The "original" (left, baseline) is resolved lazily from the multi-agent
 * service: `GET /api/artifacts/{originalArtifactId}` where `originalArtifactId`
 * is the proposed artifact's `metadata.source_artifact_id`. The "modified" (right)
 * is the proposed artifact content the caller already has in hand.
 *
 * Review UX (Cursor / VS Code style):
 *   - Sticky toolbar: file name · hunk counter · ← → navigation · Accept all / Reject all · Apply · Close
 *   - Per-hunk ✓ / ✗ inline actions in each spacer zone (never overlap code)
 *   - Full-screen side-by-side comparison via portal
 *
 * When there is no source artifact id (a freshly-created artifact with no
 * baseline) the left side is empty → whole file reads as additions ("create new").
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type * as monaco from "monaco-editor";
import { useTheme } from "next-themes";
import { DiffCodeEditor, type MonacoEditorTheme } from "@ncoder/code-editor";
import { ChevronUp, ChevronDown, Loader2, Maximize2, X as XIcon } from "lucide-react";
import { getArtifact } from "@/services/multi-agent";
import { getMonacoLanguage } from "./monacoLanguage";

export interface MultiAgentCompareViewProps {
  /**
   * UUID of the ORIGINAL/source artifact (`metadata.source_artifact_id`).
   * When null/undefined there is no baseline — the diff shows all content as new.
   */
  originalArtifactId?: string | null;
  /** Proposed artifact content (right / modified side). */
  modifiedContent: string;
  /** File name for language inference + header label. */
  fileName?: string;
  /** Salesforce metadata type for language inference. */
  metadataType?: string;
  /**
   * Called when the user finalises the comparison (Accept all / Apply).
   * Receives the final accepted text after any per-hunk merges.
   */
  onAccept?: (acceptedContent: string) => void;
  /** Called when the user discards the proposed changes. */
  onReject?: () => void;
}

export function MultiAgentCompareView({
  originalArtifactId,
  modifiedContent,
  fileName,
  metadataType,
  onAccept,
  onReject,
}: MultiAgentCompareViewProps) {
  const { resolvedTheme } = useTheme();
  const editorTheme: MonacoEditorTheme =
    resolvedTheme === "dark" ? "apex-dark" : "apex-light";

  const diffEditorRef = useRef<monaco.editor.IStandaloneDiffEditor | null>(null);
  const diffHunkStatsListenerRef = useRef<monaco.IDisposable | null>(null);
  const scrollToHunkRef = useRef<((idx: number) => void) | null>(null);

  // ── Content state ─────────────────────────────────────────────────────────
  const [originalContent, setOriginalContent] = useState<string>("");
  const [working, setWorking] = useState<string>(modifiedContent);
  const [loading, setLoading] = useState<boolean>(!!originalArtifactId);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ── Review state ──────────────────────────────────────────────────────────
  const [diffOverlayOpen, setDiffOverlayOpen] = useState(false);
  const [diffHunkTotal, setDiffHunkTotal] = useState(0);
  const [diffHunkResolved, setDiffHunkResolved] = useState(0);
  /** Tracks the currently focused hunk index (no re-render needed). */
  const currentHunkIdxRef = useRef(0);

  // ── Resolve original artifact ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    if (!originalArtifactId) {
      setOriginalContent("");
      setLoading(false);
      setLoadError(null);
      return;
    }
    setLoading(true);
    setLoadError(null);
    getArtifact(originalArtifactId)
      .then((art) => {
        if (cancelled) return;
        setOriginalContent(art.content ?? "");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setOriginalContent("");
        setLoadError(
          err && typeof err === "object" && "message" in err
            ? String((err as Error).message)
            : "Failed to load original artifact",
        );
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [originalArtifactId]);

  // Re-seed working copy when proposed content changes.
  useEffect(() => {
    setWorking(modifiedContent);
    setDiffHunkResolved(0);
    currentHunkIdxRef.current = 0;
  }, [modifiedContent]);

  // Dispose listeners on unmount.
  useEffect(() => {
    return () => {
      try { diffHunkStatsListenerRef.current?.dispose(); } catch { /* */ }
      diffHunkStatsListenerRef.current = null;
    };
  }, []);

  // Drop inline listener when overlay opens (overlay mounts its own editor).
  useEffect(() => {
    if (!diffOverlayOpen) return;
    try { diffHunkStatsListenerRef.current?.dispose(); } catch { /* */ }
    diffHunkStatsListenerRef.current = null;
  }, [diffOverlayOpen]);

  const disposeDiff = useCallback(() => {
    try { diffHunkStatsListenerRef.current?.dispose(); } catch { /* */ }
    diffHunkStatsListenerRef.current = null;
    diffEditorRef.current = null;
  }, []);

  // ── Navigation ────────────────────────────────────────────────────────────
  const navigateHunk = useCallback((delta: number) => {
    const next = Math.max(0, Math.min(diffHunkTotal - 1, currentHunkIdxRef.current + delta));
    currentHunkIdxRef.current = next;
    scrollToHunkRef.current?.(next);
  }, [diffHunkTotal]);

  // ── Bulk actions ──────────────────────────────────────────────────────────
  const finalizeAccept = useCallback(() => {
    const accepted =
      diffEditorRef.current?.getModifiedEditor().getValue() ?? working;
    disposeDiff();
    setDiffOverlayOpen(false);
    onAccept?.(accepted);
  }, [working, disposeDiff, onAccept]);

  const finalizeReject = useCallback(() => {
    disposeDiff();
    setDiffOverlayOpen(false);
    onReject?.();
  }, [disposeDiff, onReject]);

  const handleAcceptAll = useCallback(() => {
    const ed = diffEditorRef.current;
    const accepted = ed?.getModifiedEditor().getValue() ?? working;
    setOriginalContent(accepted);
    setWorking(accepted);
    setDiffHunkTotal(0);
    setDiffHunkResolved(0);
    disposeDiff();
    onAccept?.(accepted);
  }, [working, disposeDiff, onAccept]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const lang = getMonacoLanguage(fileName ?? "file.cls", metadataType);
  const diffEditorOptions = { ignoreTrimWhitespace: false };
  const hasTextDiff = originalContent !== working;
  const bulkDisabled = !hasTextDiff && diffHunkTotal === 0;
  const allReviewed = diffHunkTotal === 0 || diffHunkResolved >= diffHunkTotal;

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        <span className="text-xs">Loading original…</span>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
      {/* Load error banner (soft — falls back to "create new" diff) */}
      {loadError && (
        <div className="px-3 py-1.5 text-xs text-ma-warn-text bg-ma-warn-soft border-b shrink-0">
          Could not load original ({loadError}) — showing as new file.
        </div>
      )}

      {!diffOverlayOpen && (
        <>
          {/* ── Sticky review toolbar ─────────────────────────────────── */}
          <div className="flex items-center gap-2 px-3 py-1.5 border-b bg-background shrink-0 min-w-0">
            {/* Left: file + hunk counter */}
            <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
              {fileName && (
                <span
                  className="text-xs font-mono text-muted-foreground truncate"
                  title={fileName}
                >
                  {fileName}
                </span>
              )}
              {diffHunkTotal > 0 && (
                <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums px-1.5 py-0.5 rounded bg-muted">
                  {diffHunkResolved}/{diffHunkTotal}
                </span>
              )}
              {!originalArtifactId && (
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  new file
                </span>
              )}
            </div>

            {/* Navigation */}
            {diffHunkTotal > 1 && (
              <div className="flex items-center shrink-0">
                <button
                  type="button"
                  onClick={() => navigateHunk(-1)}
                  title="Previous change"
                  className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => navigateHunk(1)}
                  title="Next change"
                  className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {/* Divider */}
            <div className="w-px h-3.5 bg-border shrink-0" />

            {/* Actions */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                disabled={bulkDisabled}
                onClick={finalizeReject}
                title="Discard all proposed changes"
                className="text-[11px] px-2 py-0.5 rounded border border-ma-err-line text-ma-err-text hover:bg-ma-err-soft disabled:opacity-40 disabled:pointer-events-none transition-colors"
              >
                Reject all
              </button>
              <button
                type="button"
                disabled={bulkDisabled}
                onClick={handleAcceptAll}
                title="Accept every remaining change"
                className="text-[11px] px-2 py-0.5 rounded bg-ma-ok text-white disabled:opacity-40 disabled:pointer-events-none transition-colors"
              >
                Accept all
              </button>
              <button
                type="button"
                disabled={!allReviewed}
                onClick={finalizeAccept}
                title={
                  allReviewed
                    ? "Apply changes"
                    : `Review all ${diffHunkTotal} change(s) first (${diffHunkResolved} done)`
                }
                className={`text-[11px] px-2 py-0.5 rounded transition-colors ${
                  allReviewed
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "bg-muted text-muted-foreground cursor-not-allowed"
                }`}
              >
                Apply
              </button>
              <button
                type="button"
                onClick={() => setDiffOverlayOpen(true)}
                title="Side-by-side comparison"
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={finalizeReject}
                title="Close review"
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* ── Inline Monaco diff ──────────────────────────────────────── */}
          <div className="flex-1 min-h-0">
            <DiffCodeEditor
              key={`ma-compare-${originalArtifactId ?? "new"}`}
              original={originalContent}
              modified={working}
              language={lang}
              theme={editorTheme}
              viewMode="inline"
              readOnly={true}
              editorOptions={diffEditorOptions}
              inlineActions={true}
              scrollToHunkRef={scrollToHunkRef}
              onMount={(editor) => {
                diffEditorRef.current = editor;
                try { diffHunkStatsListenerRef.current?.dispose(); } catch { /* */ }
                diffHunkStatsListenerRef.current = null;
                const syncHunkTotal = () => {
                  try { setDiffHunkTotal(editor.getLineChanges()?.length ?? 0); }
                  catch { setDiffHunkTotal(0); }
                };
                diffHunkStatsListenerRef.current = editor.onDidUpdateDiff(syncHunkTotal);
                syncHunkTotal();
                requestAnimationFrame(syncHunkTotal);
              }}
              onHunkAccept={(mergedBaseline) => {
                setOriginalContent(mergedBaseline);
                setWorking(mergedBaseline);
                setDiffHunkResolved((prev) => prev + 1);
              }}
              onHunkReject={(_idx, updatedModified) => {
                setWorking(updatedModified);
                setDiffHunkResolved((prev) => prev + 1);
              }}
            />
          </div>
        </>
      )}

      {/* ── Full-screen side-by-side overlay ───────────────────────────────── */}
      {diffOverlayOpen && createPortal(
        <div className="fixed inset-0 z-[9000] bg-background/95 backdrop-blur-sm flex flex-col">
          <div className="flex items-center justify-between px-6 py-3 border-b bg-muted/50 shrink-0 gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
              <span className="shrink-0 text-sm font-semibold">Comparing changes</span>
              {fileName && (
                <span
                  className="min-w-0 truncate text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded"
                  title={fileName}
                >
                  {fileName}
                </span>
              )}
              <span className="shrink-0 text-xs text-muted-foreground">
                Original → Modified
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={finalizeReject}
                className="text-xs px-3 py-1.5 rounded border border-ma-err-line text-ma-err-text hover:bg-ma-err-soft transition-colors"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={() => {
                  const accepted =
                    diffEditorRef.current?.getModifiedEditor().getValue() ?? working;
                  disposeDiff();
                  setDiffOverlayOpen(false);
                  onAccept?.(accepted);
                }}
                className="text-xs px-3 py-1.5 rounded bg-ma-ok text-white transition-colors"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={() => {
                  diffEditorRef.current = null;
                  setDiffOverlayOpen(false);
                }}
                className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Close overlay"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <DiffCodeEditor
              key={`ma-compare-overlay-${originalArtifactId ?? "new"}`}
              original={originalContent}
              modified={working}
              language={lang}
              theme={editorTheme}
              viewMode="side-by-side"
              readOnly={true}
              editorOptions={diffEditorOptions}
              onMount={(editor) => { diffEditorRef.current = editor; }}
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default MultiAgentCompareView;
